import React from 'react';
import { useState, useEffect } from 'react';
import { 
  Building2, Users, Target, History, Award, 
  Heart, Lightbulb, Users2, BarChart, Mail, 
  HardHat, Cpu, Trophy, Presentation, ChevronDown, ChevronUp, X 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';

const AboutUs = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('leadership');

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const departments = {
    leadership: {
      title: "Leadership",
      icon: <Building2 className="w-6 h-6" />,
      email: "leadership@urbanewal.in",
      description: "Our visionary leadership team driving innovation and excellence in construction.",
      members: [
        {
          name: 'Mr Bindeshwar Prasad Singh',
          position: 'Founder & Chairman',
          description: 'With over 15 years of experience, Mr Singh founded Urbanwall Projects with a vision to revolutionize the construction landscape through innovation and quality.',
          achievements: ['15+ Years Experience', 'Industry Pioneer', 'Vision Leader'],
          photo: "/assets/logo.svg" 
        },
        {
          name: 'Shubham Singh',
          position: 'Chief Executive Officer',
          description: 'Leading our strategic initiatives with 7+ years of industry expertise, driving sustainable growth and operational excellence.',
          achievements: ['Strategic Planning', 'Innovation Leadership', 'Business Development'],
          photo: "/assets/logo.svg"
        }
      ]
    },
    sales: {
      title: "Sales & Business Development",
      icon: <BarChart className="w-6 h-6" />,
      email: "sales@urbanewal.in",
      description: "Expert team dedicated to understanding and fulfilling client needs.",
      members: [
        {
          name: 'Rahul Kumar',
          position: 'Director of Sales',
          description: 'A seasoned sales leader with 10+ years of experience in construction sales, specializing in high-value property transactions and client relationships.',
          expertise: ['Commercial Sales', 'Strategic Partnerships', 'Property Valuation'],
          achievements: ['₹100Cr+ Sales', 'Top Performer 2023', '50+ Major Deals'],
          photo: "/assets/logo.svg"
        },
        {
          name: 'Priya Sharma',
          position: 'Senior Sales Manager',
          description: 'Expert in luxury property sales with 7 years of experience in portfolio management and client consulting.',
          expertise: ['Luxury Sales', 'Client Relations', 'Market Analysis'],
          achievements: ['15+ Premium Projects', 'Client Satisfaction 98%'],
          photo: "/assets/logo.svg"
        }
      ]
    },
    tech: {
      title: "Technology & Innovation",
      icon: <Cpu className="w-6 h-6" />,
      email: "tech@urbanewal.in",
      description: "Driving digital transformation in construction through cutting-edge technology.",
      members: [
        {
          name: 'Sarthak Singh',
          position: 'Head of Technology',
          description: 'Leading our digital transformation with 5 years of expertise in construction technology and software development.',
          expertise: ['Full Stack Development', 'Cloud Architecture', 'Construction Tech'],
          achievements: ['Digital Innovation Lead', 'Tech Modernization', 'Process Automation'],
          certifications: ['AWS Certified', 'PMP', 'Agile Expert'],
          photo: "/assets/logo.svg"
        },
        {
          name: 'Tech Team',
          position: 'Development & Innovation',
          description: 'A talented team of developers and engineers building next-generation construction technology solutions.',
          expertise: ['Web Development', 'Mobile Apps', 'IoT Solutions'],
          achievements: ['10+ Digital Products', 'Innovation Awards'],
          photo: "/assets/logo.svg"
        }
      ]
    },
    operations: {
      title: "Project Operations",
      icon: <HardHat className="w-6 h-6" />,
      email: "operations@urbanewal.in",
      description: "Ensuring excellence in project execution and delivery.",
      members: [
        {
          name: 'Chandan Pal',
          position: 'Senior Operations Manager',
          description: 'Expert in project execution with focus on quality and timely delivery. Leading major construction projects across regions.',
          expertise: ['Project Management', 'Quality Control', 'Team Leadership'],
          achievements: ['12+ Projects Delivered', 'Safety Excellence Award'],
          photo: "/assets/logo.svg"
        },
        {
          name: 'Shubham Singh',
          position: 'Operations Manager',
          description: 'Specializes in on-site management and team coordination for seamless project execution.',
          expertise: ['Site Management', 'Resource Planning', 'Safety Compliance'],
          achievements: ['8+ Projects', 'Zero Incident Record'],
          photo: "/assets/logo.svg"
        }
      ]
    },
    marketing: {
      title: "Marketing & Communications",
      icon: <Presentation className="w-6 h-6" />,
      email: "marketing@urbanewal.in",
      description: "Creating impactful brand presence and market engagement.",
      members: [
        {
          name: 'Warish Ahmed',
          position: 'Head of Marketing',
          description: 'Strategic marketing leader driving brand growth and digital presence in the construction sector.',
          expertise: ['Brand Strategy', 'Digital Marketing', 'Content Strategy'],
          achievements: ['Brand Growth 200%', 'Digital Transformation'],
          photo: "/assets/logo.svg"
        },
        {
          name: 'Roshan Raj',
          position: 'Digital Marketing Specialist',
          description: 'Digital marketing expert specializing in social media and online engagement strategies.',
          expertise: ['Social Media', 'SEO', 'Content Creation'],
          achievements: ['500% Social Growth', 'Lead Generation Expert'],
          photo: "/assets/logo.svg"
        }
      ]
    }
  };

  const companyInfo = {
    mission: 'To deliver high-quality construction projects that exceed our clients expectations.',
    vision: 'To be the leading construction company known for innovation, sustainability, and quality.',
    values: [
      { text: 'Integrity', icon: <Heart className="w-6 h-6" /> },
      { text: 'Quality', icon: <Award className="w-6 h-6" /> },
      { text: 'Customer Satisfaction', icon: <Users className="w-6 h-6" /> },
      { text: 'Innovation', icon: <Lightbulb className="w-6 h-6" /> },
      { text: 'Teamwork', icon: <Users2 className="w-6 h-6" /> },
    ],
    history: 'Founded in 2000, Urbanwall Projects has been at the forefront of the construction industry, completing numerous landmark projects.',
  };

  const TeamMemberCard = ({ member, isVisible }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`bg-white rounded-2xl overflow-hidden h-full
          ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}
          ${isExpanded ? 'shadow-2xl' : 'shadow-lg hover:shadow-xl'}`}
      >
        {/* Card Header */}
        <div className="relative h-32 bg-gradient-to-r from-blue-600 to-blue-800">
          <div className="absolute inset-0 bg-grid-pattern opacity-10" />
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <h3 className="text-xl font-bold mb-1 line-clamp-1">{member.name}</h3>
            <p className="text-blue-100 text-sm line-clamp-1">{member.position}</p>
          </div>
        </div>

        {/* Card Content */}
        <div className="p-6 flex flex-col min-h-[200px]">
          {/* Description with fixed height */}
          <div className="mb-4 flex-grow">
            <p className="text-gray-600 text-sm leading-relaxed">
              {isExpanded ? member.description : (
                <span className="line-clamp-3">{member.description}</span>
              )}
            </p>
          </div>

          {/* Achievements */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-600" />
              Key Achievements
            </h4>
            <div className="flex flex-wrap gap-2">
              {member.achievements?.slice(0, isExpanded ? undefined : 2).map((achievement, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-xs
                    border border-amber-100 flex items-center gap-1.5"
                >
                  <Award className="w-3 h-3 flex-shrink-0" />
                  {achievement}
                </span>
              ))}
            </div>
          </div>

          {/* Expertise if available */}
          {member.expertise && (
            <div className="mt-4 space-y-4">
              <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <Cpu className="w-4 h-4 text-blue-600" />
                Expertise
              </h4>
              <div className="flex flex-wrap gap-2">
                {member.expertise?.slice(0, isExpanded ? undefined : 2).map((skill, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-xs
                      border border-blue-100"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Card Actions */}
          <div className="mt-6 flex gap-2">
            <motion.a
              href={`mailto:${member.email || departments[activeTab].email}`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4
                bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl
                hover:shadow-lg transition-all duration-300 text-sm font-medium"
            >
              <Mail className="w-4 h-4" />
              Contact
            </motion.a>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center justify-center w-12 rounded-xl
                bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all duration-300"
            >
              {isExpanded ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  };

  // Add PropTypes validation
  TeamMemberCard.propTypes = {
    member: PropTypes.shape({
      name: PropTypes.string.isRequired,
      position: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      photo: PropTypes.string.isRequired,
      email: PropTypes.string,
      expertise: PropTypes.arrayOf(PropTypes.string),
      achievements: PropTypes.arrayOf(PropTypes.string),
      certifications: PropTypes.arrayOf(PropTypes.string)
    }).isRequired,
    isVisible: PropTypes.bool.isRequired
  };

  return (
    <div 
      id="about" 
      className="min-h-screen bg-white pt-20"
    >
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              About <span className="text-blue-200">Urbanwall</span>
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              {companyInfo.history}
            </p>
          </div>
        </div>
      </div>

      {/* Mission, Vision, Values Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { title: 'Our Mission', text: companyInfo.mission, icon: Target },
            { title: 'Our Vision', text: companyInfo.vision, icon: Lightbulb },
            { title: 'Our Values', values: companyInfo.values, icon: Heart }
          ].map((item, idx) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.2 }}
              className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all"
            >
              <div className="flex items-center mb-4">
                <div className="p-3 bg-blue-50 rounded-xl mr-4">
                  <item.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
              </div>
              {item.values ? (
                <div className="grid grid-cols-2 gap-3">
                  {item.values.map((value, i) => (
                    <div key={i} className="flex items-center p-3 bg-gray-50 rounded-lg">
                      {value.icon}
                      <span className="ml-2 text-gray-700">{value.text}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 leading-relaxed">{item.text}</p>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Departments Section */}
      <div className="bg-gray-50 mt-24 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">
              Our Departments
            </h2>
            
            {/* Department Navigation */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {Object.entries(departments).map(([key, dept]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center px-6 py-3 rounded-lg transition-all duration-300
                    ${activeTab === key 
                      ? 'bg-blue-600 text-white shadow-md' 
                      : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                    }`}
                >
                  <span className="mr-2">{dept.icon}</span>
                  {dept.title}
                </button>
              ))}
            </div>

            {/* Department Description */}
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-16">
              {departments[activeTab].description}
            </p>
          </div>

          {/* Team Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <AnimatePresence>
              {departments[activeTab].members.map((member) => (
                <TeamMemberCard 
                  key={member.name} 
                  member={member} 
                  isVisible={isVisible}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;